import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Col, Row, Card, Badge, ProgressBar, Table } from "react-bootstrap";
import { CircleMinus, ShieldHalved, RupeeSign } from "../../../../../../../components/icons/Icons";
import RightSidebar from './RightSidebar';
import { currentFynencialYear } from '../../../../../../../services/commonServices';
import { getReviwQueueDeshbordPath } from "../../../../../../../services/config";
import { useDispatch } from "react-redux";
import { commonSaveData } from "../../../../../../../redux/slices/commonSlice";

const Overview = ({ tdsEmpList, regimeType, setRegimeType, reviewTdsDeclaration }) => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [apiData, setApiData] = useState(null);
  const [selectedRegime, setSelectedRegime] = useState("new");

  const selectedEmployee = JSON.parse(localStorage.getItem("tdsUser") || "{}");
  const currentEmployee = selectedEmployee?.staffId ? selectedEmployee : (tdsEmpList[0] || {});
  const staffId = currentEmployee.staffId || currentEmployee.staff_id;

  const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
  const userId = userData.user_id || userData.id || "123";

  useEffect(() => {
    const loadTDSData = async () => {
      if (!staffId) return;

      setLoading(true);

      try {
        const payload = {
          path: getReviwQueueDeshbordPath,
          data: {
            userId: userId,
            staffId: Number(staffId),
            financialYear: currentFynencialYear(),
          },
        };

        console.log("Calling Review Queue Dashboard API:", getReviwQueueDeshbordPath);
        console.log("Request payload:", payload);

        const response = await dispatch(commonSaveData(payload)).unwrap();

        console.log("Full API Response:", response);

        const body = response?.data || response;
        if (body && body.code === 200 && body.status === true) {
          if (body.data) {
            console.log("HR Review Queue data received successfully");
            setApiData(body.data);
          } else {
            console.warn("API returned success but no data field");
            setApiData(null);
          }
        } else {
          console.warn("API returned non-success response:", body);
          setApiData(null);
          toast.warn(body?.message || "No TDS data available for review");
        }

      } catch (error) {
        console.error("TDS Load Error:", error);
        setApiData(null);
        toast.error("Failed to load TDS review data");
      } finally {
        setLoading(false);
      }
    };

    loadTDSData();
  }, [staffId, dispatch, userId]);

  const handleChangeOldNewRegime = (type) => {
    setSelectedRegime(type);
    setRegimeType(type);
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "0";
    const num = Number(amount);
    return isNaN(num) ? "0" : num.toLocaleString('en-IN');
  };

  // Helper function to calculate values from HR Review data
  const calculateOverviewFromHRData = (hrData) => {
    if (!hrData) return {};

    if (hrData.overview) {
      const overview = hrData.overview;
      const deductions = overview.deductionsClaimed || {};
      const tax = overview.taxCalculation || {};
      return {
        grossSalary: Number(overview.grossSalary || 0),
        standardDeduction: Number(overview.standardDeduction || 0),
        totalExemptions: Number(overview.totalExemptions || 0),
        section80C: Number(deductions["80C"] || 0),
        section80D: Number(deductions["80D"] || 0),
        section80E: Number(deductions["80E"] || 0),
        section80G: Number(deductions["80G"] || 0),
        homeLoanInterest: Number(deductions["24b"] || 0),
        totalDeductions: Object.values(deductions).reduce((sum, value) => sum + Number(value || 0), 0),
        taxableIncome: Number(tax.taxableIncome || 0),
        taxBeforeRebate: Number(tax.taxBeforeRebate || 0),
        rebate87A: Number(tax.rebate87A || 0),
        cess: Number(tax.cess || 0),
        annualTax: Number(tax.annualTax || 0),
        monthlyTDS: Number(tax.monthlyTDS || 0),
      };
    }

    const sections = hrData.sections || {};
    const employeeInfo = hrData.employeeInfo || {};

    let hraExemption = 0;
    let section80C = 0;
    let section80D = 0;
    let section80E = 0;
    let section80G = 0;
    let totalDeductions = 0;
    let grossSalary = 25000 * 12; // Default fallback

    if (sections.hra && sections.hra.fields && sections.hra.fields.annualHRAExemption) {
      hraExemption = parseInt(sections.hra.fields.annualHRAExemption.value) || 0;
    }

    if (sections.deductions && sections.deductions.categories) {
      sections.deductions.categories.forEach(category => {
        const amount = parseInt(category.amount) || 0;
        if (category.section === "80C" || category.type === "section80C") {
          section80C = amount;
        } else if (category.section === "80D" || category.type === "section80D") {
          section80D = amount;
        } else if (category.type === "section80E") {
          section80E = amount;
        } else if (category.type === "section80G") {
          section80G = amount;
        }
      });

      totalDeductions = sections.deductions.totalDeduction 
        ? parseInt(sections.deductions.totalDeduction) || 0 
        : section80C + section80D + section80E + section80G;
    }

    const standardDeduction = 50000;
    const totalExemptions = hraExemption;
    const taxableIncome = Math.max(0, grossSalary - standardDeduction - totalExemptions - totalDeductions);

    let taxBeforeRebate = 0;
    let rebate87A = 0;

    if (selectedRegime === "new") {
      if (taxableIncome <= 300000) taxBeforeRebate = 0;
      else if (taxableIncome <= 600000) taxBeforeRebate = (taxableIncome - 300000) * 0.05;
      else if (taxableIncome <= 900000) taxBeforeRebate = 15000 + (taxableIncome - 600000) * 0.10;
      else if (taxableIncome <= 1200000) taxBeforeRebate = 45000 + (taxableIncome - 900000) * 0.15;
      else if (taxableIncome <= 1500000) taxBeforeRebate = 90000 + (taxableIncome - 1200000) * 0.20;
      else taxBeforeRebate = 150000 + (taxableIncome - 1500000) * 0.30;
    } else {
      if (taxableIncome <= 250000) taxBeforeRebate = 0;
      else if (taxableIncome <= 500000) taxBeforeRebate = (taxableIncome - 250000) * 0.05;
      else if (taxableIncome <= 1000000) taxBeforeRebate = 12500 + (taxableIncome - 500000) * 0.20;
      else taxBeforeRebate = 112500 + (taxableIncome - 1000000) * 0.30;
    }

    if (taxableIncome <= 500000) {
      rebate87A = Math.min(taxBeforeRebate, 12500);
    }

    const taxAfterRebate = Math.max(0, taxBeforeRebate - rebate87A);
    const cess = taxAfterRebate * 0.04;
    const annualTax = Math.round(taxAfterRebate + cess);

    return {
      grossSalary,
      standardDeduction,
      totalExemptions,
      section80C,
      section80D,
      section80E,
      section80G,
      totalDeductions,
      taxableIncome,
      taxBeforeRebate,
      rebate87A,
      cess,
      annualTax
    };
  };

  // Loading State
  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-purple" style={{ width: '3rem', height: '3rem' }} />
        <p className="mt-3 color-gray">Loading TDS Review Dashboard...</p>
      </div>
    );
  }

  // No employee selected
  if (!staffId) {
    return (
      <div className="text-center py-5">
        <div className="card bg-dark border-warning" style={{ maxWidth: '500px', margin: '0 auto' }}>
          <div className="card-header bg-warning text-dark">
            <h4 className="mb-0">No Employee Selected</h4>
          </div>
          <div className="card-body">
            <p className="color-gray">
              Please select an employee from the TDS employee list to view their tax declaration.
            </p>
            {tdsEmpList.length > 0 && (
              <button 
                className="btn btn-purple mt-3"
                onClick={() => {
                  const firstEmp = tdsEmpList[0];
                  localStorage.setItem("tdsUser", JSON.stringify(firstEmp));
                  window.location.reload();
                }}
              >
                Select First Employee
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // No data available (covers both API error and empty response)
  if (!apiData) {
    return (
      <div className="text-center py-5">
        <div className="card bg-dark border-black" style={{ maxWidth: '500px', margin: '0 auto' }}>
          <div className="card-header bg-black text-white">
            <h4 className="mb-0">No TDS Review Data Available</h4>
          </div>
          <div className="card-body">
            <p className="color-gray">
              Unable to load TDS review data for the selected employee.
            </p>
            <div className="mt-3">
              <p className="small color-gray">
                Employee ID: <strong>{staffId}</strong><br />
                Financial Year: <strong>{currentFynencialYear()}</strong>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  console.log("Rendering with HR Review data:", apiData);

  const employeeInfo = apiData.employeeInfo || {};
  const sections = apiData.sections || {};
  const overallStatus = apiData.reviewStatus || apiData.overallStatus || {};
  const financialYear = employeeInfo.financialYear || currentFynencialYear();

  const calculatedValues = calculateOverviewFromHRData(apiData);
  
  const {
    grossSalary,
    standardDeduction,
    totalExemptions,
    section80C,
    section80D,
    section80E,
    section80G,
    homeLoanInterest,
    totalDeductions,
    taxableIncome,
    taxBeforeRebate,
    rebate87A,
    cess,
    annualTax
  } = calculatedValues;

  const monthlyTDS = calculatedValues.monthlyTDS ?? Math.round(annualTax / 12);

  return (
    <Row>
      <Col sm="9" className="erd-main" style={{ paddingTop: "0" }}>
        {/* Employee Header */}
        <div className="mb-4">
          <h3 className="color-white font-weight-600 mb-2">
            {employeeInfo.employeeName || "Employee"}
          </h3>
          <div className="d-flex flex-wrap gap-3 color-gray font-14 mb-3">
            <span>ID: <strong className="color-white">{employeeInfo.employeeId || staffId}</strong></span>
            <span>PAN: <strong className="color-white">{employeeInfo.panNumber || employeeInfo.employeePAN || "N/A"}</strong></span>
            <span>FY: <strong className="color-white">{financialYear}</strong></span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className="color-white">Current Regime:</span>
            <span className={`badge ${selectedRegime === 'new' ? 'bg-info' : 'bg-warning'} text-white`}>
              {selectedRegime.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Overall Review Status */}
        <div className="row mb-4">
          <div className="col-12">
            <Card className="bg-dark border-0">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap">
                  <div>
                    <h5 className="color-white mb-2">Overall Review Status</h5>
                    <p className="color-gray mb-0">
                      Completion: <strong className="color-white">{overallStatus.completionPercentage || 0}%</strong>
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge bg={
                      (overallStatus.statusText || overallStatus.finalStatus) === 'Approved' || overallStatus.finalStatus === 'approved' ? 'success' :
                      (overallStatus.statusText || overallStatus.finalStatus) === 'Rejected' || overallStatus.finalStatus === 'rejected' ? 'danger' : 'warning'
                    } className="px-3 py-2">
                      {(overallStatus.statusText || overallStatus.finalStatus || 'Pending').toUpperCase()}
                    </Badge>
                    <p className="color-gray small mt-1">
                      Last Updated: {overallStatus.lastUpdated || 'N/A'}
                    </p>
                  </div>
                </div>
                <ProgressBar 
                  now={overallStatus.completionPercentage || 0} 
                  style={{ height: '8px', marginTop: '15px' }}
                  variant="success"
                />
              </Card.Body>
            </Card>
          </div>
        </div>

        {/* Three Cards Row */}
        <div className="row mb-4">
          <div className="col-md-4 mb-3">
            <Card className="erd-card border-0 bg-dark">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h6 className="color-gray mb-1">Gross Salary</h6>
                    <h2 className="text-purple font-weight-700 mb-0">
                      ₹{formatCurrency(grossSalary)}
                    </h2>
                  </div>
                  <RupeeSign class_name="text-purple" size="24" />
                </div>
                <div className="color-gray small">Annual CTC</div>
              </Card.Body>
            </Card>
          </div>

          <div className="col-md-4 mb-3">
            <Card className="erd-card border-0 bg-dark">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h6 className="color-gray mb-1">Standard Deduction</h6>
                    <h2 className="green-text font-weight-700 mb-0">
                      ₹{formatCurrency(standardDeduction)}
                    </h2>
                  </div>
                  <CircleMinus class_name="green-text" size="24" />
                </div>
                <div className="color-gray small">u/s 16(ia)</div>
              </Card.Body>
            </Card>
          </div>

          <div className="col-md-4 mb-3">
            <Card className="erd-card border-0 bg-dark">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h6 className="color-gray mb-1">Total Exemptions</h6>
                    <h2 className="text-info font-weight-700 mb-0">
                      ₹{formatCurrency(totalExemptions)}
                    </h2>
                  </div>
                  <ShieldHalved class_name="text-info" size="24" />
                </div>
                <div className="color-gray small">HRA + Others</div>
              </Card.Body>
            </Card>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="row">
          <Col md={6} className="mb-4">
            <Card className="erd-card border-0 bg-dark h-100">
              <Card.Header className="bg-transparent border-bottom border-gray-700">
                <h5 className="color-white mb-0">Deductions Claimed</h5>
              </Card.Header>
              <Card.Body>
                {section80C > 0 && (
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="color-gray">80C (PPF, ELSS, etc.)</span>
                      <Badge bg="dark" className="font-weight-600">₹{formatCurrency(section80C)}</Badge>
                    </div>
                    <ProgressBar now={(section80C / 150000) * 100} style={{ height: '6px' }} variant="success" />
                    <small className="color-gray">Limit: ₹1,50,000</small>
                  </div>
                )}

                {section80D > 0 && (
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="color-gray">80D (Health Insurance)</span>
                      <Badge bg="dark" className="font-weight-600">₹{formatCurrency(section80D)}</Badge>
                    </div>
                    <ProgressBar now={(section80D / 25000) * 100} style={{ height: '6px' }} variant="info" />
                    <small className="color-gray">Limit: ₹25,000</small>
                  </div>
                )}

                {section80E > 0 && (
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="color-gray">80E (Education Loan Interest)</span>
                      <Badge bg="dark" className="font-weight-600">₹{formatCurrency(section80E)}</Badge>
                    </div>
                    <ProgressBar now={Math.min(section80E / 200000 * 100, 100)} style={{ height: '6px' }} variant="warning" />
                    <small className="color-gray">Max: ₹2,00,000</small>
                  </div>
                )}

                {section80G > 0 && (
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="color-gray">80G (Donations)</span>
                      <Badge bg="dark" className="font-weight-600">₹{formatCurrency(section80G)}</Badge>
                    </div>
                    <ProgressBar now={Math.min(section80G / 50000 * 100, 100)} style={{ height: '6px' }} variant="danger" />
                    <small className="color-gray">Varies by donation type</small>
                  </div>
                )}

                {homeLoanInterest > 0 && (
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="color-gray">24(b) Home Loan Interest</span>
                      <Badge bg="dark" className="font-weight-600">â‚¹{formatCurrency(homeLoanInterest)}</Badge>
                    </div>
                    <ProgressBar now={Math.min(homeLoanInterest / 200000 * 100, 100)} style={{ height: '6px' }} variant="primary" />
                    <small className="color-gray">Limit: â‚¹2,00,000</small>
                  </div>
                )}

                {totalDeductions === 0 && (
                  <div className="text-center py-4">
                    <p className="color-gray">No deductions claimed</p>
                  </div>
                )}

                <div className="border-top border-gray-700 pt-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="color-white font-weight-600">Total Deductions</span>
                    <Badge bg={totalDeductions > 0 ? "success" : "secondary"} className="font-weight-600 px-3 py-2">
                      ₹{formatCurrency(totalDeductions)}
                    </Badge>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6} className="mb-4">
            <Card className="erd-card border-0 bg-dark h-100">
              <Card.Header className="bg-transparent border-bottom border-gray-700 d-flex justify-content-between align-items-center">
                <h5 className="color-white mb-0">Tax Calculation</h5>
                <Badge bg="info" className="font-weight-600">
                  {selectedRegime.toUpperCase()} REGIME
                </Badge>
              </Card.Header>
              <Card.Body>
                <Table borderless className="color-white mb-0">
                  <tbody>
                    <tr>
                      <td className="color-gray py-2">Taxable Income</td>
                      <td className="text-right font-weight-600">₹{formatCurrency(taxableIncome)}</td>
                    </tr>
                    <tr>
                      <td className="color-gray py-2">Tax Before Rebate</td>
                      <td className="text-right font-weight-600">₹{formatCurrency(taxBeforeRebate)}</td>
                    </tr>
                    {rebate87A > 0 && (
                      <tr>
                        <td className="color-gray py-2">87A Rebate</td>
                        <td className="text-right green-text font-weight-600">-₹{formatCurrency(rebate87A)}</td>
                      </tr>
                    )}
                    <tr>
                      <td className="color-gray py-2">Cess (4%)</td>
                      <td className="text-right font-weight-600">₹{formatCurrency(cess)}</td>
                    </tr>
                    <tr className="border-top border-gray-700">
                      <td className="color-white font-weight-600 pt-3">Annual Tax</td>
                      <td className="text-right text-purple font-weight-700 h4 mb-0 pt-3">
                        ₹{formatCurrency(annualTax)}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="2" className="pt-2 pb-0">
                        <div className="bg-dark-dark p-3 rounded">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <div className="color-gray small">Monthly TDS</div>
                              <div className="color-white font-weight-600">₹{formatCurrency(monthlyTDS)}</div>
                            </div>
                            <div className="text-right">
                              <div className="color-gray small">Per Month</div>
                              <div className="text-purple font-weight-600">
                                {monthlyTDS > 0 ? "Deducted" : "No TDS"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </div>

        {/* Sections Review Status */}
        <div className="row mt-4">
          <div className="col-12">
            <Card className="erd-card border-0 bg-dark">
              <Card.Header className="bg-transparent border-bottom border-gray-700">
                <h5 className="color-white mb-0">Sections Review Status</h5>
              </Card.Header>
              <Card.Body>
                <div className="row">
                  {sections.hra && (
                    <div className="col-md-4 mb-3">
                      <div className="bg-dark-dark p-3 rounded h-100">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="color-white mb-0">{sections.hra.title || "HRA"}</h6>
                          <Badge bg={
                            sections.hra.status === 'approved' ? 'success' :
                            sections.hra.status === 'rejected' ? 'danger' : 'warning'
                          }>
                            {sections.hra.status?.toUpperCase() || 'PENDING'}
                          </Badge>
                        </div>
                        <ProgressBar 
                          now={sections.hra.completionPercentage || 0} 
                          style={{ height: '5px', marginBottom: '10px' }}
                          variant="info"
                        />
                        <small className="color-gray">{sections.hra.completionPercentage || 0}% Complete</small>
                        {sections.hra.fields?.annualHRAExemption && (
                          <div className="mt-2">
                            <small className="color-gray">HRA Exemption:</small>
                            <div className="color-white">₹{formatCurrency(sections.hra.fields.annualHRAExemption.value)}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {sections.homeLoan && (
                    <div className="col-md-4 mb-3">
                      <div className="bg-dark-dark p-3 rounded h-100">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="color-white mb-0">{sections.homeLoan.title || "Home Loan"}</h6>
                          <Badge bg={
                            sections.homeLoan.status === 'approved' ? 'success' :
                            sections.homeLoan.status === 'rejected' ? 'danger' : 'warning'
                          }>
                            {sections.homeLoan.status?.toUpperCase() || 'PENDING'}
                          </Badge>
                        </div>
                        <ProgressBar 
                          now={sections.homeLoan.completionPercentage || 0} 
                          style={{ height: '5px', marginBottom: '10px' }}
                          variant="info"
                        />
                        <small className="color-gray">{sections.homeLoan.completionPercentage || 0}% Complete</small>
                      </div>
                    </div>
                  )}

                  {sections.deductions && (
                    <div className="col-md-4 mb-3">
                      <div className="bg-dark-dark p-3 rounded h-100">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="color-white mb-0">{sections.deductions.title || "Deductions"}</h6>
                          <Badge bg={
                            sections.deductions.status === 'approved' ? 'success' :
                            sections.deductions.status === 'rejected' ? 'danger' : 'warning'
                          }>
                            {sections.deductions.status?.toUpperCase() || 'PENDING'}
                          </Badge>
                        </div>
                        <ProgressBar 
                          now={sections.deductions.completionPercentage || 0} 
                          style={{ height: '5px', marginBottom: '10px' }}
                          variant="info"
                        />
                        <small className="color-gray">{sections.deductions.completionPercentage || 0}% Complete</small>
                        {sections.deductions.totalDeduction && (
                          <div className="mt-2">
                            <small className="color-gray">Total Deductions:</small>
                            <div className="color-white">₹{formatCurrency(sections.deductions.totalDeduction)}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
      </Col>

      <Col sm="3">
        <RightSidebar
          staffId={staffId}
          reviewTdsDeclaration={reviewTdsDeclaration}
          haldleChangeOldNewRegime={handleChangeOldNewRegime}
          apiData={apiData}
          regimeType={selectedRegime}
        />
      </Col>
    </Row>
  );
};

export default Overview;
